import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DetectDeviceService } from './detect-device.service';
import { DeviceType } from '@core/models/enums/common.enum';
import { Router } from '@angular/router';
import { getSaucerLogActionByRoute } from '@core/configs';
import { ROUTE_PATH } from '@core/constants';
import { SaucerLogService } from '@core/services/saucer-logs/saucer-log.service';

export interface FullscreenState {
  isFullscreen: boolean;
}
@Injectable({
  providedIn: 'root'
})

export class FullscreenService {
  // Biến public để truy cập trực tiếp trong component
  public isLockPage: string = "init";

  // CSS pseudo-fullscreen (mobile/tablet): tránh xung đột Fullscreen API với bàn phím ảo
  private readonly pseudoFullscreenClass = 'pseudo-fullscreen';
  private readonly pseudoFullscreenVisibleClass = 'pseudo-fullscreen-visible';
  private readonly pseudoFullscreenDraggingClass = 'pseudo-fullscreen-dragging';
  private readonly pseudoFullscreenClosingClass = 'pseudo-fullscreen-closing';
  // Khớp với thời lượng transition khai báo trong layout.component.scss, dùng làm fallback dọn dẹp.
  private readonly enterTransitionMs = 250;
  private readonly exitTransitionMs = 200;
  private isPseudoFullscreen = false;
  private pseudoFullscreenElement: HTMLElement | null = null;

  // Swipe-down-to-exit cho pseudo-fullscreen (thay thế nút thoát vốn chỉ hiện khi hover)
  private readonly swipeDownThreshold = 100;
  private readonly dragActivationThreshold = 10;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartScrollTop = 0;
  private isDraggingToClose = false;
 
  public fullscreenStateSubject = new BehaviorSubject<FullscreenState>({
    isFullscreen: false
  });

  public fullscreenState$ = this.fullscreenStateSubject.asObservable();
  public isLockPageSubject= new BehaviorSubject<string>("init");
  isLockPage$ = this.isLockPageSubject.asObservable();

  // Subject phát sự kiện khi fullscreen thay đổi
  private fullscreenChangeSubject = new Subject<void>();

  // Observable cho component lắng nghe sự kiện fullscreen thay đổi
  fullscreenChange$ = this.fullscreenChangeSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private detectDeviceService: DetectDeviceService,
    private router: Router,
    private saucerLogService: SaucerLogService,
  ) {
    // Lắng nghe sự kiện thay đổi fullscreen cho các trình duyệt khác nhau
    document.addEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange.bind(this));
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange.bind(this));
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange.bind(this));

    window.addEventListener('resize', () => {
      this.ngZone.run(() => {
        const current = this.fullscreenStateSubject.getValue();
        this.fullscreenStateSubject.next({
          ...current
        });
      });
    });
  }

  /**
   * Hàm xử lý khi có thay đổi fullscreen
   * Cập nhật trạng thái fullscreen và phát sự kiện để các component có thể nhận diện
   */
  private onFullscreenChange(): void {
    // báo cho Angular biết cần cập nhật lại giao diện
    this.ngZone.run(() => {
      const isFull = !!document.fullscreenElement;
      const current = this.fullscreenStateSubject.getValue();
      let fullscreenHeight = window.innerHeight;
      const fullscreenElement = document.fullscreenElement as HTMLElement;
      if (fullscreenElement) {
        fullscreenHeight = fullscreenElement.getBoundingClientRect().height;
      }

      // Ghi log khi trạng thái fullscreen thay đổi.
      // Bathe Management serves ADMISSION/COMMUTE/WEEKLY_ADJUST depending on runtime state, not resolvable from the URL alone -> the screen logs its own fullscreen change.
      const isBatheManagementRoute = this.router.url.startsWith(`/${ROUTE_PATH.BATHE_MANAGEMENT}`);
      if (current.isFullscreen !== isFull && !isBatheManagementRoute) {
        const matchedAction = getSaucerLogActionByRoute(this.router.url);
        this.saucerLogService.action(
          {
            content: JSON.stringify({ isFullScreen: isFull })
          },
          {
            action: matchedAction?.FULL_SCREEN
          }
        );
      }

      this.fullscreenStateSubject.next({
        ...current,
        isFullscreen: isFull
      });

      const doc = document as any;
      if (!isFull) {
        doc.body.classList.remove('fullscreen-mode');
      } else {
        doc.body.classList.add('fullscreen-mode');
      }
    });
  }

  /**
   * Hàm bật/tắt fullscreen cho một phần tử HTML cụ thể
   * @param element Phần tử cần bật fullscreen
   */
  toggleFullscreen(element: HTMLElement) {
    const doc = document as any;
    const el = element as any;
    const isIPad = /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
    if (isIPad) {
      doc.body.classList.add('ipad');
    }

    // Mobile/tablet (gồm iPad): dùng CSS fullscreen để bàn phím ảo hoạt động khi focus input.
    if (this.shouldUsePseudoFullscreen()) {
      if (this.isPseudoFullscreen) {
        this.exitPseudoFullscreen();
      } else {
        this.enterPseudoFullscreen(element);
      }
      return;
    }

    const isFullscreen = doc.fullscreenElement || doc.webkitFullscreenElement

    if (!isFullscreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen().catch((err: any) => {
          alert(err);
        });
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
      doc.body.classList.add('fullscreen-mode');
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
      doc.body.classList.remove('fullscreen-mode');
    }
  }

  /**
   * Hàm thoát fullscreen nếu đang ở chế độ fullscreen
   */
  exitFullScreen() {
    if (this.isPseudoFullscreen) {
      this.exitPseudoFullscreen();
      return;
    }
    const doc = document as any;
    if (doc.fullscreenElement) {
      doc.exitFullscreen().then(() => {
        doc.body.classList.remove('fullscreen-mode');
      }).catch((err: any) => console.error(`Error exiting fullscreen: ${err}`));
    }
  }

  /**
   * Thiết bị cảm ứng (mobile/tablet, gồm iPad) không thể hiển thị bàn phím ảo khi
   * Fullscreen API đang bật, nên dùng CSS fullscreen thay thế.
   */
  private shouldUsePseudoFullscreen(): boolean {
    return this.detectDeviceService.getDeviceType() !== DeviceType.DESKTOP;
  }

  private enterPseudoFullscreen(element: HTMLElement): void {
    this.pseudoFullscreenElement = element;
    element.style.transform = '';
    element.classList.add(this.pseudoFullscreenClass);
    document.body.classList.add('fullscreen-mode');
    element.addEventListener('touchstart', this.onPseudoFullscreenTouchStart, { passive: true });
    element.addEventListener('touchmove', this.onPseudoFullscreenTouchMove, { passive: true });
    element.addEventListener('touchend', this.onPseudoFullscreenTouchEnd, { passive: true });
    this.isPseudoFullscreen = true;
    this.emitFullscreenState(true);

    // Double rAF: đảm bảo trình duyệt ghi nhận trạng thái translateY(100%) ban đầu
    // trước khi thêm class "visible", để transition slide-up thực sự chạy.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.classList.add(this.pseudoFullscreenVisibleClass);
      });
    });
  }

  private exitPseudoFullscreen(): void {
    const element = this.pseudoFullscreenElement;
    if (!element) {
      return;
    }
    element.removeEventListener('touchstart', this.onPseudoFullscreenTouchStart);
    element.removeEventListener('touchmove', this.onPseudoFullscreenTouchMove);
    element.removeEventListener('touchend', this.onPseudoFullscreenTouchEnd);
    document.body.classList.remove('fullscreen-mode');
    this.isPseudoFullscreen = false;
    this.pseudoFullscreenElement = null;
    this.emitFullscreenState(false);

    // Trượt xuống hết màn hình (exit nhanh hơn enter) rồi mới dọn class/inline style.
    element.classList.remove(this.pseudoFullscreenDraggingClass);
    element.classList.add(this.pseudoFullscreenClosingClass);
    element.classList.remove(this.pseudoFullscreenVisibleClass);
    element.style.transform = '';

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) {
        return;
      }
      cleaned = true;
      element.removeEventListener('transitionend', cleanup);
      element.classList.remove(this.pseudoFullscreenClass, this.pseudoFullscreenClosingClass);
      element.style.transform = '';
    };
    element.addEventListener('transitionend', cleanup);
    // Fallback: prefers-reduced-motion tắt transition nên transitionend không bắn ra.
    setTimeout(cleanup, this.exitTransitionMs + 50);
  }

  // Chỉ coi là swipe-to-exit khi bắt đầu từ đầu vùng cuộn (scrollTop = 0),
  // để không xung đột với thao tác cuộn nội dung bên trong (form, danh sách...).
  private onPseudoFullscreenTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartScrollTop = this.pseudoFullscreenElement?.scrollTop ?? 0;
    this.isDraggingToClose = false;
  };

  // Kéo-theo-ngón-tay theo thời gian thực: cảm giác "nắm" được nội dung, không phải chỉ ẩn/hiện tức thì.
  private onPseudoFullscreenTouchMove = (event: TouchEvent): void => {
    const element = this.pseudoFullscreenElement;
    if (!element || this.touchStartScrollTop > 0) {
      return;
    }
    const touch = event.touches[0];
    const deltaY = touch.clientY - this.touchStartY;
    const deltaX = Math.abs(touch.clientX - this.touchStartX);

    if (deltaY <= 0 || deltaX > deltaY) {
      if (this.isDraggingToClose) {
        this.isDraggingToClose = false;
        element.classList.remove(this.pseudoFullscreenDraggingClass);
        element.style.transform = '';
      }
      return;
    }

    if (!this.isDraggingToClose && deltaY > this.dragActivationThreshold) {
      this.isDraggingToClose = true;
      element.classList.add(this.pseudoFullscreenDraggingClass);
    }

    if (this.isDraggingToClose) {
      const maxDrag = element.getBoundingClientRect().height || window.innerHeight;
      const clampedDeltaY = Math.min(deltaY, maxDrag);
      element.style.transform = `translateY(${clampedDeltaY}px)`;
    }
  };

  private onPseudoFullscreenTouchEnd = (event: TouchEvent): void => {
    const element = this.pseudoFullscreenElement;
    if (!element || !this.isDraggingToClose) {
      return;
    }
    this.isDraggingToClose = false;
    element.classList.remove(this.pseudoFullscreenDraggingClass);

    const touch = event.changedTouches[0];
    const deltaY = touch.clientY - this.touchStartY;
    if (deltaY > this.swipeDownThreshold) {
      this.exitPseudoFullscreen();
    } else {
      // Chưa vượt ngưỡng: snap-back về vị trí mở, transition tự nội suy từ vị trí đang kéo dở.
      element.style.transform = '';
    }
  };

  // CSS fullscreen không phát sự kiện fullscreenchange, nên tự phát trạng thái cho component.
  private emitFullscreenState(isFullscreen: boolean): void {
    this.ngZone.run(() => {
      const current = this.fullscreenStateSubject.getValue();
      this.fullscreenStateSubject.next({ ...current, isFullscreen });
    });
  }

  removeListener(){
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
  }
}
